import React, { useContext, useCallback } from 'react'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import LePackageUpgradeCard from 'components/OfferPage/PackageUpgrade/LePackageUpgradeCard'
import { OfferPackageOptionView } from 'selectors/offerPage/offerPageSelectors'
import OfferPageStateTrackingContext from 'contexts/OfferPage/offerPageStateTrackingContext'
import * as Analytics from 'analytics/analytics'
import PackageUpgradeModal from 'components/OfferPage/PackageUpgrade/PackageUpgradeModal'
import ModalContext from 'contexts/ModalContext'
import { isFlashHotel } from 'lib/offer/offerTypes'
import { useIsMobileScreen } from 'lib/web/deviceUtils'

export const OfferUpgradeModalContext = React.createContext<(upgrade: OfferPackageOptionView, packageOption: OfferPackageOptionView, hasFlightsSelected: boolean) => Promise<OfferPackageOptionView>>((_u, p) => Promise.resolve(p))

interface Props {
  children: React.ReactNode;
  offer: App.Offer;
}

function OfferUpgradeModalContextProvider({ children, offer }: Props) {
  const showModal = useContext(ModalContext)
  const isMobile = useIsMobileScreen()

  const { trackEvent } = useContext(OfferPageStateTrackingContext)
  const openModal = useCallback(async(upgrade: OfferPackageOptionView, packageOption: OfferPackageOptionView, hasFlightsSelected: boolean): Promise<OfferPackageOptionView> => {
    const upgradePackageView: App.PackageOptionView = {
      offer,
      // no need to account for flight prices, they are always the same for the same day
      priceDifference: upgrade.hotelPrice - packageOption.hotelPrice,
      memberPriceDifference: upgrade.hotelMemberPrice - packageOption.hotelMemberPrice,
      price: upgrade.hotelPrice,
      memberPrice: upgrade.hotelMemberPrice,
      package: upgrade.package,
      value: upgrade.hotelValue,
      flightPrice: upgrade.flightPrice,
      flightsAvailable: upgrade.flightsAvailable,
    }

    return showModal<boolean>(
      <PackageUpgradeModal>
        <LePackageUpgradeCard
          packageUpgrade={upgradePackageView}
          selectedPackage={packageOption}
          hasFlightsSelected={hasFlightsSelected}
        />
      </PackageUpgradeModal>,
    ).then(didUpgrade => {
      if (didUpgrade) {
        trackEvent('UPGRADE_MODAL_ACCEPTED')
        Analytics.trackClientEvent({
          subject: 'upgrade_modal',
          action: 'select_upgrade',
          category: 'logging',
          type: 'interaction',
        })
        if (isFlashHotel(offer) && isMobile) {
          Analytics.trackClientEvent({
            subject: 'upgrade_yes',
            action: 'click',
            category: 'offer_funnel',
            type: 'interaction',
          })
        }
        return upgrade
      } else {
        trackEvent('UPGRADE_MODAL_REJECTED')
        Analytics.trackClientEvent({
          subject: 'upgrade_modal',
          action: 'no_upgrade',
          category: 'logging',
          type: 'interaction',
        })
        if (isFlashHotel(offer) && isMobile) {
          Analytics.trackClientEvent({
            subject: 'upgrade_no',
            action: 'click',
            category: 'offer_funnel',
            type: 'interaction',
          })
        }
        return packageOption
      }
    })
  }, [offer, showModal, trackEvent, isMobile])

  return <OfferUpgradeModalContext.Provider
    value={openModal}
    children={children}
  />
}

export default withOfferPageAndReduxState((state) => ({
  offer: state.offer,
}))(OfferUpgradeModalContextProvider)
